(function () {
  class TabsCollectionCarousel {
    constructor(root) {
      this.root = root;
      this.sectionId = root.id;
      this.mount = root.querySelector("[data-tabs-content-mount]");
      this.cache = new Map();
      this.loadingBlockId = null;
      this.activeBlockId = null;
      this.shimmerHtml = null;
    }

    buildShimmerHtml() {
      if (this.shimmerHtml) return this.shimmerHtml;

      const template = document.getElementById(
        `tabs-shimmer-${this.sectionId}`,
      );
      if (template?.innerHTML?.trim()) {
        this.shimmerHtml = template.innerHTML.trim();
        return this.shimmerHtml;
      }

      this.shimmerHtml = `<div class="tab-shimmer-slider hdt-collection-products hdt-slider hdt-slide-spacing-x hdt-relative hdt-slides-3" aria-hidden="true"><div class="hdt-slider__viewport hdt-relative"><div class="hdt-slider__container">${Array.from({ length: 8 })
        .map(
          () =>
            `<div class="hdt-slider__slide tab-shimmer-slide"><div class="tab-shimmer-card"><div class="tab-shimmer-image hdt-ratio" style="--aspect-ratioapt:1;"></div><div class="tab-shimmer-line"></div></div></div>`,
        )
        .join("")}</div></div></div>`;
      return this.shimmerHtml;
    }

    getTabButtons() {
      return Array.from(
        this.root.querySelectorAll("button.hdt-tabs-item-title[data-tab-load]"),
      );
    }

    getEffectiveLoadType(button) {
      const loadType = button.dataset.tabLoad || "panel";
      if (loadType === "template" && !button.getAttribute("data-tab-get")) {
        return button.getAttribute("data-tab-fetch") ? "panel" : "template";
      }
      return loadType;
    }

    init() {
      if (!this.mount) return;

      this.initPreloadedTab();

      this.root.addEventListener(
        "click",
        (event) => {
          const button = event.target.closest(
            "button.hdt-tabs-item-title[data-tab-load]",
          );
          if (!button || !this.root.contains(button)) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.activate(button);
        },
        true,
      );

      if (this.root.dataset.tabsAutoLoad !== "false" && !this.activeBlockId) {
        const defaultButton =
          this.root.querySelector(
            'button.hdt-tabs-item-title[data-tab-load][aria-current="true"]',
          ) || this.getTabButtons()[0];

        if (defaultButton) {
          this.activate(defaultButton, { initial: true });
        }
      }
    }

    initPreloadedTab() {
      const preloaded = this.mount.querySelector("[data-tab-preloaded]");
      if (!preloaded) return;

      const blockId = preloaded.dataset.blockId;
      if (!blockId) return;

      const button = this.root.querySelector(
        `button.hdt-tabs-item-title[data-block-id="${CSS.escape(blockId)}"]`,
      );

      this.cache.set(blockId, preloaded.outerHTML);
      this.activeBlockId = blockId;

      if (button) {
        this.setActiveButton(button);
      }

      this.afterPanelMount(preloaded);

      if (
        this.getEffectiveLoadType(button) === "template" &&
        button.getAttribute("data-tab-get")
      ) {
        const target =
          preloaded.querySelector(".dynamic_loaded_items") ||
          preloaded.querySelector(`#contant_id-${CSS.escape(blockId)}`);
        if (target && !target.dataset.loaded) {
          this.fetchTemplateContent(button, target);
        }
      }
    }

    setActiveButton(button) {
      this.root
        .querySelectorAll("button.hdt-tabs-item-title")
        .forEach((btn) => btn.setAttribute("aria-current", "false"));
      button.setAttribute("aria-current", "true");

      const tabBgColor = button.getAttribute("data-tab_bg_color");
      const tabsContentEl = this.root.querySelector(".hdt-tabs-content-box");
      if (tabBgColor && tabsContentEl) {
        tabsContentEl.style.setProperty("--carousel_tabs_bg_color", tabBgColor);
      }

      if (
        this.root.closest("[data-tabs-carousel-section]") &&
        typeof window.applyTabsCarouselHeaderStyles === "function"
      ) {
        window.applyTabsCarouselHeaderStyles(button);
      }
    }

    setMountHtml(html) {
      if (this.mount) {
        this.mount.innerHTML = html;
      }
    }

    wrapPanelHtml(blockId, panelHtml) {
      return `<div class="hdt-tab-item" tab-item tab-selected data-block-id="${blockId}">${panelHtml}</div>`;
    }

    showShimmer(blockId) {
      this.setMountHtml(this.wrapPanelHtml(blockId, this.buildShimmerHtml()));
    }

    afterPanelMount(tabItem) {
      tabItem?.querySelectorAll("video.hdt-featured-video").forEach((video) => {
        const source = video.querySelector("source");
        const lazyUrl = source?.dataset?.src || source?.getAttribute("data-src");
        if (source && lazyUrl && !source.src) {
          source.src = lazyUrl;
          source.removeAttribute("data-src");
          video.load();
        }
        video.play().catch(() => {});
        video.closest(".hdt-ccmp-fetured-video")?.classList.add("is-video-loaded");
      });

      if (typeof window.initProductQuantityControls === "function") {
        window.initProductQuantityControls(this.mount);
      }

      tabItem?.querySelectorAll("hdt-slider").forEach((slider) => {
        if (typeof slider.reInit === "function") {
          slider.reInit();
        }
      });

      tabItem?.dispatchEvent(
        new CustomEvent("tabcontent:loaded", {
          bubbles: true,
          detail: { target: tabItem },
        }),
      );
    }

    normalizeFetchUrl(url) {
      if (!url) return null;

      if (/^https?:\/\//i.test(url)) {
        return url;
      }

      const root = (window.Shopify?.routes?.root || "/").replace(/\/?$/, "");
      const path = url.startsWith("/") ? url : `/${url}`;
      return `${root}${path}`;
    }

    buildRecentlyUrl(button) {
      const recentlyKey = "theme4:recently:id";
      const ids = (localStorage.getItem(recentlyKey) || "")
        .split(",")
        .filter(Boolean);
      if (!ids.length) return null;
      return this.normalizeFetchUrl(button.getAttribute("data-tab-fetch"));
    }

    resolveFetchUrl(button) {
      const loadType = this.getEffectiveLoadType(button);

      if (loadType === "recently") {
        return this.buildRecentlyUrl(button);
      }

      if (loadType === "template") {
        const pageUrl = button.getAttribute("data-tab-get");
        if (!pageUrl) return null;
        return this.normalizeFetchUrl(pageUrl);
      }

      return this.normalizeFetchUrl(button.getAttribute("data-tab-fetch"));
    }

    extractPanelHtml(html, button) {
      const loadType = this.getEffectiveLoadType(button);

      if (loadType === "template") {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        const targetId = button.getAttribute("data-tab-target");
        const target = targetId
          ? tempDiv.querySelector(`#${CSS.escape(targetId)}`)
          : null;
        const pageContent =
          target ||
          tempDiv.querySelector("#MainContent") ||
          tempDiv.querySelector(".dynamic_loaded_items") ||
          tempDiv;
        return pageContent.innerHTML;
      }

      const doc = new DOMParser().parseFromString(html, "text/html");
      const sectionNode =
        doc.getElementById(`shopify-section-${this.sectionId}`) ||
        doc.querySelector(`#${CSS.escape(this.sectionId)}`) ||
        doc.body;

      const panel =
        sectionNode.querySelector("[data-tabs-ajax-panel]") ||
        sectionNode.querySelector("[data-tab-preloaded]") ||
        sectionNode.querySelector(".hdt-tab-item") ||
        sectionNode.querySelector("hdt-slider") ||
        sectionNode.querySelector("product-recently") ||
        sectionNode.querySelector(".buy-again-products") ||
        sectionNode;

      if (panel?.querySelector?.("[data-tabs-ajax-panel]")) {
        return panel.querySelector("[data-tabs-ajax-panel]").innerHTML.trim();
      }

      if (panel === sectionNode && panel !== doc.body) {
        return panel.innerHTML.trim();
      }

      if (panel === doc.body) {
        return panel.innerHTML.trim();
      }

      return panel.outerHTML;
    }

    async fetchTemplateContent(button, target) {
      const fetchUrl = this.resolveFetchUrl(button);
      if (!fetchUrl || !target) return;

      try {
        const response = await fetch(fetchUrl);
        const html = await response.text();
        target.innerHTML = this.extractPanelHtml(html, button);
        target.dataset.loaded = "true";

        if (typeof window.initProductQuantityControls === "function") {
          window.initProductQuantityControls(this.mount);
        }

        const tabItem = this.mount.querySelector("[tab-item]");
        tabItem?.dispatchEvent(
          new CustomEvent("tabcontent:loaded", {
            bubbles: true,
            detail: { target: tabItem },
          }),
        );
      } catch (error) {
        console.error("Template tab load failed:", error);
      }
    }

    async activate(button, { initial = false } = {}) {
      const blockId = button.getAttribute("data-block-id");
      const loadType = this.getEffectiveLoadType(button);

      if (!blockId || !this.mount) return;

      if (
        !initial &&
        button.getAttribute("aria-current") === "true" &&
        this.activeBlockId === blockId
      ) {
        return;
      }

      this.setActiveButton(button);

      if (this.cache.has(blockId)) {
        this.setMountHtml(this.cache.get(blockId));
        this.activeBlockId = blockId;
        const tabItem = this.mount.querySelector("[tab-item]");
        this.afterPanelMount(tabItem);

        if (loadType === "template") {
          const target =
            tabItem?.querySelector(".dynamic_loaded_items") ||
            tabItem?.querySelector(`#contant_id-${CSS.escape(blockId)}`);
          if (target && !target.dataset.loaded && button.getAttribute("data-tab-get")) {
            await this.fetchTemplateContent(button, target);
          }
        }

        return;
      }

      if (loadType === "recently" && !this.buildRecentlyUrl(button)) {
        this.setMountHtml(this.wrapPanelHtml(blockId, ""));
        this.activeBlockId = blockId;
        return;
      }

      if (loadType === "template" && !button.getAttribute("data-tab-get")) {
        this.setMountHtml(
          this.wrapPanelHtml(blockId, "<p>Tab template not configured.</p>"),
        );
        this.activeBlockId = blockId;
        return;
      }

      if (this.loadingBlockId === blockId) return;

      this.loadingBlockId = blockId;
      this.showShimmer(blockId);

      try {
        const fetchUrl = this.resolveFetchUrl(button);
        if (!fetchUrl) {
          this.setMountHtml(
            this.wrapPanelHtml(blockId, "<p>Tab content unavailable.</p>"),
          );
          return;
        }

        const response = await fetch(fetchUrl);
        const html = await response.text();
        let panelHtml = this.extractPanelHtml(html, button);

        if (loadType === "template") {
          const wrapped = this.wrapPanelHtml(
            blockId,
            `<div class="dynamic_loaded_items" id="contant_id-${blockId}">${panelHtml}</div>`,
          );
          this.cache.set(blockId, wrapped);
          this.setMountHtml(wrapped);
        } else {
          const wrapped = this.wrapPanelHtml(blockId, panelHtml);
          this.cache.set(blockId, wrapped);
          this.setMountHtml(wrapped);
        }

        this.activeBlockId = blockId;
        this.afterPanelMount(this.mount.querySelector("[tab-item]"));
      } catch (error) {
        console.error("Tabs carousel load failed:", error);
        this.setMountHtml(
          this.wrapPanelHtml(blockId, "<p>Failed to load tab content.</p>"),
        );
      } finally {
        this.loadingBlockId = null;
      }
    }
  }

  function initSection(root) {
    if (!root || root.dataset.tabsCarouselInit === "true") return;
    root.dataset.tabsCarouselInit = "true";
    const instance = new TabsCollectionCarousel(root);
    instance.init();
    root.__tabsCollectionCarousel = instance;
  }

  function initAll(scope = document) {
    scope.querySelectorAll("[data-tabs-collection-carousel]").forEach(initSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target) initAll(event.target);
  });

  window.TabsCollectionCarousel = TabsCollectionCarousel;
})();
